import React, { useState, useCallback, useContext } from 'react';
import { Handle, Position, useReactFlow, useEdges, useNodes } from 'reactflow';
import { ExclamationTriangleIcon, Pencil1Icon } from '@radix-ui/react-icons';
import { TbArrowsSplit2 } from 'react-icons/tb';
import { GrConfigure } from 'react-icons/gr';
import './SettleNode.css';
import SettleConfigModal, { settleBankingFeeText, settleBankingTypeLabel } from './SettleConfigModal';
import { usePipeline } from '../../../context/PipelineContext';
import { NodeMappingContext } from '../../Canvas/Canvas';
import { notifyError } from '../../../errors/notifyError';
import { reconfigureNode, updateNodePosition as persistNodePosition } from '../../../api/api';

// The Settle node wired directly into `id`, if any
const upstreamSettleId = (id, nodes, edges) => {
  const inputEdge = edges.find((edge) => edge.target === id);
  const upstream = inputEdge ? nodes.find((node) => node.id === inputEdge.source) : null;
  return upstream && upstream.type === 'settle' ? upstream.id : null;
};

// 1-based position in the Settle chain: the number of Settle nodes from the
// Consumption node up to and including this one. Priority is the wiring.
export const settlePriority = (id, nodes, edges) => {
  let count = 1;
  const seen = new Set([id]);
  let next = upstreamSettleId(id, nodes, edges);
  while (next && !seen.has(next)) {
    seen.add(next);
    count += 1;
    next = upstreamSettleId(next, nodes, edges);
  }
  return count;
};

const SettleNode = ({ id, data, selected }) => {
  const { name, sourceLabel, bankingType, bankingFee, generationFileName } = data;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfiguring, setIsConfiguring] = useState(false);
  const reactFlowInstance = useReactFlow();
  const edges = useEdges();
  const nodes = useNodes();
  const { session, addSettleNode, updateSelectedNode } = usePipeline();
  const { updateNodeMapping, getNodeMapping } = useContext(NodeMappingContext);

  const isConfigured = data.isConfigured || data.isApiConfigured || !!generationFileName;
  const hasInputConnection = edges.some((edge) => edge.target === id);
  const priority = settlePriority(id, nodes, edges);

  const getInputNodeName = useCallback(() => {
    const inputEdge = edges.find((edge) => edge.target === id);
    if (!inputEdge) return null;
    const sourceNode = nodes.find((node) => node.id === inputEdge.source);
    if (sourceNode?.data?.name) return sourceNode.data.name;
    const nodeMapping = getNodeMapping();
    return nodeMapping[inputEdge.source] || inputEdge.source;
  }, [edges, id, nodes, getNodeMapping]);

  const inputNodeName = getInputNodeName();

  // Rewiring the chain (reordering sources) leaves the node needing a reconfigure
  const needsReconfiguration =
    data.needsReconfiguration ||
    data.isApiConfigured === false ||
    (isConfigured && data.inputNodeName && inputNodeName && data.inputNodeName !== inputNodeName) ||
    (isConfigured && data.inputNodeName && !hasInputConnection);

  const getNodePosition = useCallback(() => {
    const node = nodes.find((n) => n.id === id);
    return node ? node.position : { x: 0, y: 0 };
  }, [nodes, id]);

  const handleReconfigureInput = async () => {
    if (!session.pipeline_name || !name) return;
    const currentInputNodeName = getInputNodeName();
    if (!currentInputNodeName) {
      notifyError(new Error('Please connect an input to this settle node before reconfiguring it.'), {
        toastId: 'settle.connectInputReconfigure',
      });
      return;
    }

    try {
      setIsConfiguring(true);
      await reconfigureNode(session.pipeline_name, name, currentInputNodeName);

      if (session.pipeline_name !== 'Unsaved_Pipeline') {
        const position = getNodePosition();
        await persistNodePosition(session.pipeline_name, name, position.x, position.y);
      }

      reactFlowInstance.setNodes((nds) =>
        nds.map((node) => (node.id !== id ? node : {
          ...node,
          data: {
            ...node.data,
            inputNodeName: currentInputNodeName,
            needsReconfiguration: false,
            isConfigured: true,
            isApiConfigured: true,
          },
        }))
      );
    } catch (error) {
      console.error('Error reconfiguring settle node input:', error);
      notifyError(error);
    } finally {
      setIsConfiguring(false);
    }
  };

  const openConfigModal = useCallback(() => {
    if (!hasInputConnection) {
      notifyError(new Error('Please connect the Consumption node or the previous Settle node before configuring this node.'), {
        toastId: 'settle.connectInput',
      });
      return;
    }
    setIsModalOpen(true);
  }, [hasInputConnection]);

  const closeConfigModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleConfigured = useCallback(async (configData) => {
    setIsConfiguring(true);
    try {
      const { name: configuredName, sourceLabel: label, bankingType: type, bankingFee: fee, generationFileName: fileName } = configData;
      const currentInputNodeName = getInputNodeName();
      const position = getNodePosition();

      updateNodeMapping(id, configuredName);
      addSettleNode(configuredName, currentInputNodeName, { x: position.x, y: position.y }, {
        sourceLabel: label,
        bankingType: type,
        bankingFee: fee,
        generationFileName: fileName,
      });

      reactFlowInstance.setNodes((nds) =>
        nds.map((node) => (node.id !== id ? node : {
          ...node,
          data: {
            ...node.data,
            name: configuredName,
            sourceLabel: label,
            bankingType: type,
            bankingFee: fee,
            generationFileName: fileName,
            inputNodeName: currentInputNodeName,
            needsReconfiguration: false,
            isConfigured: true,
            isApiConfigured: true,
          },
        }))
      );

      if (session.selected_node === id) {
        updateSelectedNode(configuredName);
      }

      if (session.pipeline_name && session.pipeline_name !== 'Unsaved_Pipeline') {
        await persistNodePosition(session.pipeline_name, configuredName, position.x, position.y);
      }
    } catch (error) {
      console.error('Error configuring settle node:', error);
      notifyError(error);
    } finally {
      setIsConfiguring(false);
      closeConfigModal();
    }
  }, [id, reactFlowInstance, getInputNodeName, getNodePosition, closeConfigModal, updateNodeMapping, addSettleNode, session, updateSelectedNode]);

  const headerState = needsReconfiguration ? 'needs-reconfiguration' : isConfigured ? 'configured' : 'not-configured';

  return (
    <>
      <div className={`settle-node ${selected ? 'selected' : headerState}`}>
        <div className={`settle-node-header ${headerState}`}>
          <div className="settle-node-header-title">
            <div className="settle-node-type">Settle</div>
            {name && isConfigured && <div className="settle-node-name-display">{name}</div>}
          </div>
          <div className="settle-node-header-icons">
            {needsReconfiguration ? (
              <button
                onClick={handleReconfigureInput}
                className="settle-config-button"
                title="Reconfigure input node"
                aria-label="Reconfigure input node"
                disabled={!hasInputConnection || isConfiguring}
              >
                <GrConfigure className="settle-icon" />
              </button>
            ) : (
              <button
                onClick={openConfigModal}
                className="settle-config-button"
                title={isConfigured ? 'Edit Configuration' : hasInputConnection ? 'Configure Settle' : 'Connect an input first'}
                aria-label={isConfigured ? 'Edit Configuration' : 'Configure Settle'}
                disabled={!hasInputConnection || isConfiguring}
              >
                {isConfigured ? <Pencil1Icon className="settle-icon" /> : <ExclamationTriangleIcon className="settle-icon" />}
              </button>
            )}
            <TbArrowsSplit2 className="settle-icon" />
          </div>
        </div>
        <div className="settle-node-content">
          <div className="settle-text-xs">
            {!hasInputConnection && <div className="settle-text-warning">Connect an input to configure</div>}
            {hasInputConnection && !isConfigured && <div className="settle-text-warning">Parameters not configured</div>}
            {needsReconfiguration && <div className="settle-text-warning">Input changed. Reconfigure required</div>}
            {isConfiguring && <div className="settle-text-info">Configuring...</div>}
            {isConfigured && (
              <>
                <div className="settle-priority-badge">Priority {priority}</div>
                <div><strong>{sourceLabel || name}</strong></div>
                <div>{settleBankingTypeLabel(bankingType)}</div>
                <div>Fee: {settleBankingFeeText(bankingType, bankingFee)}</div>
                <div className="settle-file" title={generationFileName}>File: {generationFileName || 'N/A'}</div>
              </>
            )}
          </div>
          <div className="settle-flex">
            <Handle
              type="target"
              position={Position.Left}
              id="input"
              className={`settle-connection-point ${isConfigured ? 'configured' : 'not-configured'}`}
              data-maxconnections="1"
            />
            <Handle
              type="source"
              position={Position.Right}
              id="output"
              className={`settle-connection-point ${isConfigured ? 'configured-output' : 'not-configured'}`}
              isConnectable={isConfigured || needsReconfiguration}
              data-maxconnections="1"
            />
          </div>
        </div>
      </div>

      <SettleConfigModal
        isOpen={isModalOpen}
        onClose={closeConfigModal}
        onConfigured={handleConfigured}
        initialData={{
          name,
          sourceLabel,
          bankingType,
          bankingFee,
          generationFileName,
          inputNodeName: getInputNodeName(),
          position: getNodePosition(),
          isEditing: isConfigured && Boolean(name),
        }}
      />
    </>
  );
};

export default SettleNode;
